import React, { useEffect, useMemo, useRef, useState } from 'react';
import { chartTokens } from '../charts/chart-tokens';
import { formatCompact } from '../charts/chart-utils';
import './donut-chart.scss';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  /** Optional stroke color. Falls back to chartTokens.donutPalette by index. */
  color?: string;
}

export interface DonutChartProps {
  data: DonutSegment[];
  /** Square pixel size. Radius is auto-calculated from size and strokeWidth. */
  size: number;
  /** Segment stroke thickness in px (design: 28). */
  strokeWidth?: number;
  /** Stroke thickness while hovered (design: 32, 150ms ease-out). */
  strokeWidthHovered?: number;
  /** Two-line center label shown when nothing is hovered. */
  centerLabel?: { primary: string; secondary?: string };
  ariaLabel: string;
  ariaDescription?: string;
  onSegmentHover?: (segment: DonutSegment) => void;
  onSegmentLeave?: () => void;
  /** Currently-hovered segment key — drives stroke-width highlight. */
  hoveredKey?: string | null;
}

const ANIMATION_DURATION = 800;
const easeOut = (p: number): number => p * (2 - p);

const segmentColor = (s: DonutSegment, idx: number): string =>
  s.color ?? chartTokens.donutPalette[idx % chartTokens.donutPalette.length] ?? '#ccc';

/**
 * Stroke-based donut chart. Each segment is a `<circle>` with `stroke-dasharray`
 * trimmed to its arc fraction. Segments start at 12 o'clock (achieved via dashoffset).
 * Hover stroke-width jumps from 28→32 with 150ms CSS transition.
 * Entry animation: 800ms ease-out dash extension on mount.
 */
export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  size,
  strokeWidth = 28,
  strokeWidthHovered = 32,
  centerLabel,
  ariaLabel,
  ariaDescription,
  onSegmentHover,
  onSegmentLeave,
  hoveredKey,
}) => {
  const [animProgress, setAnimProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    setAnimProgress(0);
    startRef.current = null;
    const tick = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / ANIMATION_DURATION);
      setAnimProgress(easeOut(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [data]);

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  const r = (size - strokeWidth - 4) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  // Offset each segment's dashoffset by -circumference/4 so they start at 12 o'clock
  const topOffset = circumference / 4;

  const segments = useMemo(() => {
    if (total === 0) return [];
    let arcSoFar = 0;
    return data.map((seg) => {
      const fraction = seg.value / total;
      const dashLen = fraction * circumference * animProgress;
      // dashoffset: negative = advance start position clockwise
      const dashOffset = -(topOffset + arcSoFar);
      arcSoFar += fraction * circumference;
      return { seg, dashLen, dashOffset, fraction };
    });
  }, [data, total, circumference, topOffset, animProgress]);

  const hoveredSegment = hoveredKey
    ? data.find((d) => d.key === hoveredKey) ?? null
    : null;

  return (
    <svg
      className="donut-chart"
      role="img"
      width={size}
      height={size}
      aria-label={ariaLabel}
      viewBox={`0 0 ${size} ${size}`}
    >
      <title>{ariaLabel}</title>
      {ariaDescription ? <desc>{ariaDescription}</desc> : null}

      {segments.map(({ seg, dashLen, dashOffset, fraction }, idx) => {
        const isHovered = hoveredKey === seg.key;
        const sw = isHovered ? strokeWidthHovered : strokeWidth;
        return (
          <circle
            key={seg.key}
            className="donut-chart__segment"
            role="graphics-symbol"
            aria-label={`${seg.label}: ${formatCompact(seg.value)} (${(fraction * 100).toFixed(1)}%)`}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={segmentColor(seg, idx)}
            strokeWidth={sw}
            strokeDasharray={`${dashLen} ${circumference - dashLen}`}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-width 150ms ease-out' }}
            onMouseEnter={onSegmentHover ? () => onSegmentHover(seg) : undefined}
            onMouseLeave={onSegmentLeave}
          />
        );
      })}

      {/* Center label */}
      <g className="donut-chart__center" aria-hidden="true">
        {hoveredSegment ? (
          <>
            <text
              className="donut-chart__center-hovered-pct"
              textAnchor="middle"
              x={cx}
              y={cy - 10}
            >
              {total > 0 ? `${((hoveredSegment.value / total) * 100).toFixed(1)}%` : '—'}
            </text>
            <text
              className="donut-chart__center-hovered-label"
              textAnchor="middle"
              x={cx}
              y={cy + 6}
            >
              {hoveredSegment.label}
            </text>
            <text
              className="donut-chart__center-hovered-count"
              textAnchor="middle"
              x={cx}
              y={cy + 20}
            >
              {formatCompact(hoveredSegment.value)}
            </text>
          </>
        ) : centerLabel ? (
          <>
            <text
              className="donut-chart__center-primary"
              textAnchor="middle"
              x={cx}
              y={cy + 6}
            >
              {centerLabel.primary}
            </text>
            {centerLabel.secondary ? (
              <text
                className="donut-chart__center-secondary"
                textAnchor="middle"
                x={cx}
                y={cy + 22}
              >
                {centerLabel.secondary}
              </text>
            ) : null}
          </>
        ) : null}
      </g>
    </svg>
  );
};

DonutChart.displayName = 'DonutChart';
